// Team Cup engine for the isolated G-New Features sandbox.
//
// FULLY SELF-CONTAINED: its own copy of the match-points + handicap logic,
// ported so the sandbox never imports from — nor can be broken by — any live
// Team Cup file (teamCupScoring.js / teamCupData.js / TeamCupTab|Score|
// Leaderboard). Pure functions, no React, no Firebase. Unit-testable under
// `node --test`. Model:
//   • Multi-team best-ball points race. Per hole, each team's best ball is the
//     lowest NET (or GROSS when handicaps off) among its players; lowest team
//     best ball wins 1 pt, ties split equally.
//   • OFFICIAL: hole points only (max 18). CUSTOM: + 1 bonus per nine won
//     (tied nine = split), max 20.
//   • Off-the-Lowest (net only): subtract the field's lowest playing handicap
//     from everyone so the low player plays scratch.
//   • Side bets (always GROSS): Scats (field-wide low gross, zero-sum) and
//     CTH (par-3 closest-to-pin, unclaimed pots carry to remaining par 3s).

export const DEFAULT_STROKE_INDEX = [
  10, 2, 16, 6, 14, 4, 18, 8, 12,
  5, 1, 13, 3, 15, 7, 17, 9, 11,
]

// ── Stroke allocation ────────────────────────────────────────────────────────

/** Per-hole strokes[18] for a course handicap and stroke index. Negative CH
 *  (plus-handicaps) gives strokes back on the easiest holes. */
export function allocateStrokes(courseHcp, strokeIndex = DEFAULT_STROKE_INDEX) {
  const out = new Array(18).fill(0)
  const ch = Number(courseHcp)
  if (!Number.isFinite(ch) || ch === 0) return out
  const sign = ch >= 0 ? 1 : -1
  let remaining = Math.abs(ch)
  const order = Array.from({ length: 18 }, (_, i) => ({ hole: i, rank: strokeIndex?.[i] ?? DEFAULT_STROKE_INDEX[i] }))
    .sort((a, b) => a.rank - b.rank)
  const sequence = ch >= 0 ? order : [...order].reverse()
  let layer = 0
  while (remaining > 0) {
    const take = Math.min(18, remaining)
    for (let i = 0; i < take; i++) out[sequence[i].hole] += sign
    remaining -= take
    layer += 1
    if (layer > 4) break
  }
  return out
}

/** Build `{ playerId: strokes[18] }` from a `{ playerId: courseHandicap }` map. */
export function strokesForRound(courseHandicaps, strokeIndex = DEFAULT_STROKE_INDEX) {
  const out = {}
  for (const [pid, ch] of Object.entries(courseHandicaps || {})) {
    out[pid] = allocateStrokes(Number(ch) || 0, strokeIndex)
  }
  return out
}

/** Off-the-Lowest: subtract the field's lowest handicap from every player so
 *  the low player plays scratch. Returns a NEW map; input untouched. Since
 *  `lo` is the minimum, `ph − lo ≥ 0` for everyone (no flooring needed).
 *  Empty map → empty map; single player → 0. */
export function applyOffTheLowest(courseHandicaps) {
  const entries = Object.entries(courseHandicaps || {})
  if (entries.length === 0) return {}
  const lo = Math.min(...entries.map(([, ch]) => Number(ch) || 0))
  const out = {}
  for (const [pid, ch] of entries) out[pid] = (Number(ch) || 0) - lo
  return out
}

// ── Net helpers ──────────────────────────────────────────────────────────────

export function netForHole(gross, strokesReceived) {
  const g = Number(gross)
  if (!Number.isFinite(g) || g <= 0) return null
  return g - (Number(strokesReceived) || 0)
}

function netForPlayer(scoresByHole, hole, pid, strokesByPlayer) {
  const g = scoresByHole?.[hole]?.[pid]
  if (g == null) return null
  const n = Number(g)
  if (!Number.isFinite(n) || n <= 0) return null
  const sr = strokesByPlayer?.[pid]?.[hole - 1] ?? 0
  return netForHole(n, sr)
}

function grossForPlayer(scoresByHole, hole, pid) {
  const g = scoresByHole?.[hole]?.[pid]
  if (g == null) return null
  const n = Number(g)
  return Number.isFinite(n) && n > 0 ? n : null
}

/** Lowest net (or gross when handicaps off) on `hole` across the team's
 *  players. null when no player has a score. */
function teamBestBall(team, hole, scoresByHole, strokesByPlayer, handicapsOn) {
  const vals = (team.playerIds || [])
    .map(pid => handicapsOn
      ? netForPlayer(scoresByHole, hole, pid, strokesByPlayer)
      : grossForPlayer(scoresByHole, hole, pid))
    .filter(v => v != null)
  if (vals.length === 0) return null
  return Math.min(...vals)
}

// ── Match-points engine ──────────────────────────────────────────────────────

/**
 * Compute a full Team Cup match across N teams over 18 holes.
 * Returns { teamPoints, holePoints, ninePoints:{front,back}, holes[18], valid }.
 */
export function computeTeamMatchResults({
  teams, scoresByHole, strokesByPlayer, handicapsOn = false, scoringSystem = 'official',
}) {
  const ts = Array.isArray(teams) ? teams.filter(t => (t.playerIds || []).length > 0) : []
  const emptyHoles = Array.from({ length: 18 }, () => ({ winnerId: null, bests: {} }))
  const teamPoints = {}
  const holePoints = {}
  const ninePoints = {}
  ts.forEach(t => { teamPoints[t.id] = 0; holePoints[t.id] = 0; ninePoints[t.id] = { front: 0, back: 0 } })
  if (ts.length < 2) {
    return { teamPoints, holePoints, ninePoints, holes: emptyHoles, valid: false }
  }

  const holes = []
  for (let i = 0; i < 18; i++) {
    const hole = i + 1
    const bests = {}
    ts.forEach(t => { bests[t.id] = teamBestBall(t, hole, scoresByHole, strokesByPlayer, handicapsOn) })
    const definedVals = Object.values(bests).filter(v => v != null)
    // Hole decides once every team has at least one entered score.
    if (definedVals.length !== ts.length) {
      holes.push({ winnerId: null, bests })
      continue
    }
    const min = Math.min(...definedVals)
    const winners = ts.filter(t => bests[t.id] === min)
    if (winners.length === 1) {
      holes.push({ winnerId: winners[0].id, bests })
      holePoints[winners[0].id] += 1
    } else {
      holes.push({ winnerId: 'tie', bests })
      const share = 1 / winners.length
      winners.forEach(t => { holePoints[t.id] += share })
    }
  }

  if (scoringSystem === 'custom') {
    const wins = (start, end) => {
      const ws = {}
      ts.forEach(t => { ws[t.id] = 0 })
      for (let h = start; h <= end; h++) {
        const { winnerId } = holes[h]
        if (winnerId == null) return null
        if (winnerId === 'tie') {
          const min = Math.min(...Object.values(holes[h].bests))
          const tied = ts.filter(t => holes[h].bests[t.id] === min)
          tied.forEach(t => { ws[t.id] += 1 / tied.length })
        } else {
          ws[winnerId] += 1
        }
      }
      return ws
    }
    const award = (wsMap, key) => {
      if (!wsMap) return
      const max = Math.max(...Object.values(wsMap))
      const leaders = Object.entries(wsMap).filter(([, v]) => v === max).map(([k]) => k)
      const share = 1 / leaders.length
      leaders.forEach(id => { ninePoints[id][key] += share })
    }
    award(wins(0, 8), 'front')
    award(wins(9, 17), 'back')
  }

  ts.forEach(t => { teamPoints[t.id] = holePoints[t.id] + ninePoints[t.id].front + ninePoints[t.id].back })
  return { teamPoints, holePoints, ninePoints, holes, valid: true }
}

// ── Scats — field-wide low gross, zero-sum ────────────────────────────────────

/**
 * Scats across every player. Winner (outright low gross) collects `perHole`
 * from each other player per hole won; ties cancel. Dollars only when
 * roundFinal. Returns { holesWonByPlayer, payoutsByPlayer, perHole[18] }.
 */
export function computeScatPayouts({ playerIds, scoresByGroup, perHole = 0, roundFinal = false }) {
  const holesWonByPlayer = {}
  const payoutsByPlayer = {}
  const allIds = Array.isArray(playerIds) ? playerIds : []
  allIds.forEach(pid => { holesWonByPlayer[pid] = 0; payoutsByPlayer[pid] = 0 })
  const perHoleRows = []

  for (let h = 1; h <= 18; h++) {
    const entries = []
    for (const pid of allIds) {
      let v = null
      for (const grpScores of Object.values(scoresByGroup || {})) {
        const g = grpScores?.[h]?.[pid]
        if (g != null) { v = Number(g); break }
      }
      if (Number.isFinite(v) && v > 0) entries.push({ pid, gross: v })
    }
    if (entries.length < allIds.length) {
      perHoleRows.push({ hole: h, winnerId: null, gross: null })
      continue
    }
    const min = Math.min(...entries.map(e => e.gross))
    const winners = entries.filter(e => e.gross === min)
    if (winners.length !== 1) {
      perHoleRows.push({ hole: h, winnerId: 'tie', gross: min })
      continue
    }
    holesWonByPlayer[winners[0].pid] += 1
    perHoleRows.push({ hole: h, winnerId: winners[0].pid, gross: min })
  }

  if (roundFinal && perHole > 0) {
    const decidedHoles = perHoleRows.filter(r => r.winnerId && r.winnerId !== 'tie').length
    allIds.forEach(pid => {
      const won = holesWonByPlayer[pid] || 0
      const losses = decidedHoles - won
      payoutsByPlayer[pid] = (won * perHole * (allIds.length - 1)) - (losses * perHole)
    })
  }

  return { holesWonByPlayer, payoutsByPlayer, perHole: perHoleRows }
}

// ── CTH — per-par-3 closest-to-pin with carryover ─────────────────────────────

/**
 * CTH payouts. Base pot per par 3; an all-None par 3 carries its pot equally
 * across remaining par-3 pots (leftoverBucket if none remain). cthByHole[hole]
 * accepts a playerId|'none' string, or a { [group]: { v, t } } map (latest t
 * wins). Returns { perPlayer, perHole, leftoverBucket }.
 */
export function computeCthValues({ pars, cthByHole, groupCodes = [], perPar3 = 0 }) {
  const par3Holes = []
  for (let i = 0; i < 18; i++) if (pars?.[i] === 3) par3Holes.push(i + 1)
  const pots = par3Holes.map(() => perPar3)
  const perPlayer = {}
  const perHole = []
  let leftoverBucket = 0
  const bump = (pid, amt) => { perPlayer[pid] = (perPlayer[pid] || 0) + amt }
  const expected = Math.max(1, groupCodes.length)

  for (let i = 0; i < par3Holes.length; i++) {
    const hole = par3Holes[i]
    const subs = cthByHole?.[hole]
    const potThis = pots[i]
    if (subs == null) {
      perHole.push({ hole, par: 3, winnerId: null, paid: 0, carryFromHere: 0 })
      continue
    }
    let entries = []
    if (typeof subs === 'string') {
      entries = [{ v: subs, t: 0 }]
    } else if (typeof subs === 'object') {
      for (const raw of Object.values(subs)) {
        if (typeof raw === 'string') entries.push({ v: raw, t: 0 })
        else if (raw && typeof raw.v === 'string') entries.push({ v: raw.v, t: Number(raw.t) || 0 })
      }
    }
    if (entries.length < expected) {
      perHole.push({ hole, par: 3, winnerId: null, paid: 0, carryFromHere: 0 })
      continue
    }
    const real = entries.filter(e => e.v !== 'none')
    if (real.length === 0) {
      const remaining = par3Holes.length - 1 - i
      if (remaining > 0) {
        const each = potThis / remaining
        for (let j = i + 1; j < par3Holes.length; j++) pots[j] += each
      } else {
        leftoverBucket += potThis
      }
      perHole.push({ hole, par: 3, winnerId: 'none', paid: 0, carryFromHere: potThis })
      continue
    }
    real.sort((a, b) => b.t - a.t)
    const winnerId = real[0].v
    bump(winnerId, potThis)
    perHole.push({ hole, par: 3, winnerId, paid: potThis, carryFromHere: 0 })
  }

  return { perPlayer, perHole, leftoverBucket }
}

// ── Sample adapter ────────────────────────────────────────────────────────────

/**
 * Flatten SAMPLE_TOURNAMENT (flights → 2-player teams with per-player gross)
 * into the Team Cup model. Each sample team becomes a competing team; its two
 * players get synthetic ids `${teamId}_p1/2`. Returns everything the engine
 * needs: { teams, players, scoresByHole, courseHandicaps }.
 */
export function teamCupFieldFromSample(sample) {
  const teams = []
  const players = []
  const scoresByHole = {}
  const courseHandicaps = {}
  for (const flight of Object.values(sample?.flights || {})) {
    for (const [teamId, t] of Object.entries(flight?.teams || {})) {
      const info = t.info || {}
      const names = info.players || []
      const phs = info.playingHandicaps || []
      const pids = []
      names.forEach((nm, i) => {
        const pid = `${teamId}_p${i + 1}`
        pids.push(pid)
        players.push({ id: pid, name: nm, teamId, ph: Number(phs[i]) || 0 })
        courseHandicaps[pid] = Number(phs[i]) || 0
      })
      teams.push({ id: teamId, name: info.name || teamId, playerIds: pids, flightName: flight?.info?.name })
      for (let h = 1; h <= 18; h++) {
        const s = t.scores?.[h]
        if (!s) continue
        if (!scoresByHole[h]) scoresByHole[h] = {}
        if (Number.isFinite(Number(s.p1score))) scoresByHole[h][pids[0]] = Number(s.p1score)
        if (pids[1] && Number.isFinite(Number(s.p2score))) scoresByHole[h][pids[1]] = Number(s.p2score)
      }
    }
  }
  return { teams, players, scoresByHole, courseHandicaps }
}

export { netForPlayer, grossForPlayer, teamBestBall }
